import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Plus, Pencil, Trash, Trophy, CalendarDots, MapPin, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = [
  { value: 'competição', label: 'Competição' },
  { value: 'treino especial', label: 'Treino Especial' },
  { value: 'social', label: 'Evento Social' }
];

const emptyEvent = {
  title: '',
  description: '',
  event_type: 'competição',
  date: new Date().toISOString().split('T')[0],
  start_time: '',
  end_time: '',
  location: ''
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [formData, setFormData] = useState(emptyEvent);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [showPast, setShowPast] = useState(false);
  const navigate = useNavigate();

  const fetchEvents = async () => {
    try {
      const { data } = await axios.get(`${API}/events`, { params: { include_past: showPast }, withCredentials: true });
      setEvents(data);
    } catch (e) {
      toast.error('Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPast]);

  const handleOpenCreate = () => {
    setCurrentEvent(null);
    setFormData({
      ...emptyEvent,
      date: format(selectedDate, 'yyyy-MM-dd')
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (event) => {
    setCurrentEvent(event);
    setFormData({
      title: event.title,
      description: event.description || '',
      event_type: event.event_type,
      date: event.date,
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      location: event.location || ''
    });
    setDialogOpen(true);
  };

  const handleOpenDelete = (event) => {
    setCurrentEvent(event);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        start_time: formData.start_time || null,
        end_time: formData.end_time || null,
        location: formData.location || null
      };
      if (currentEvent) {
        await axios.put(`${API}/events/${currentEvent.id}`, payload, { withCredentials: true });
        toast.success('Evento atualizado com sucesso');
      } else {
        await axios.post(`${API}/events`, payload, { withCredentials: true });
        toast.success('Evento criado com sucesso');
      }
      setDialogOpen(false);
      fetchEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar evento');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/events/${currentEvent.id}`, { withCredentials: true });
      toast.success('Evento removido com sucesso');
      setDeleteDialogOpen(false);
      fetchEvents();
    } catch (e) {
      toast.error('Erro ao remover evento');
    }
  };

  const filteredEvents = events.filter(e => {
    if (filter !== 'all' && e.event_type !== filter) return false;
    return true;
  });

  const eventDates = [...new Set(events.map(e => e.date))];
  const todayEvents = filteredEvents.filter(e => e.date === format(selectedDate, 'yyyy-MM-dd'));
  const upcomingEvents = filteredEvents.filter(e => e.date > format(new Date(), 'yyyy-MM-dd')).slice(0, 5);

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'competição': return 'bg-[#E85D04]';
      case 'treino especial': return 'bg-[#0077B6]';
      case 'social': return 'bg-[#2D6A4F]';
      default: return 'bg-[#73706A]';
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="events-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Eventos</h1>
            <p className="text-[#73706A] mt-1">Calendário de eventos e competições</p>
          </div>
          <Button
            onClick={handleOpenCreate}
            className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
            data-testid="add-event-btn"
          >
            <Plus size={20} className="mr-2" />
            Novo Evento
          </Button>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-sm text-[#73706A]">Filtrar por:</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="event-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              {eventTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <label className="flex items-center gap-2 text-sm text-[#73706A] cursor-pointer ml-auto sm:ml-2" data-testid="show-past-events-label">
            <input
              type="checkbox"
              checked={showPast}
              onChange={(e) => setShowPast(e.target.checked)}
              className="w-4 h-4 rounded border-[#C7C0B0] text-[#E85D04] focus:ring-[#E85D04]"
              data-testid="show-past-events-checkbox"
            />
            Mostrar eventos passados
          </label>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
            <h2 className="section-title mb-4">Calendário</h2>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              locale={ptBR}
              className="rounded-md border-0"
              modifiers={{
                hasEvent: (date) => eventDates.includes(format(date, 'yyyy-MM-dd'))
              }}
              modifiersStyles={{
                hasEvent: { backgroundColor: '#E85D04', color: 'white', borderRadius: '50%' }
              }}
              data-testid="events-calendar"
            />

            {/* Selected Date Events */}
            <div className="mt-6 pt-6 border-t border-[#E6E2D8]">
              <h3 className="font-medium text-[#1A1A1A] mb-3">
                {format(selectedDate, "d 'de' MMMM", { locale: ptBR })}
              </h3>
              {todayEvents.length === 0 ? (
                <p className="text-sm text-[#73706A]">Nenhum evento neste dia</p>
              ) : (
                <div className="space-y-2">
                  {todayEvents.map((event, index) => (
                    <div
                      key={event.id}
                      className="flex items-center gap-2 p-2 bg-[#F5F2EB] rounded text-sm cursor-pointer hover:bg-[#E6E2D8] transition-colors"
                      onClick={() => navigate('/courts')}
                      data-testid={`calendar-event-${index}`}
                    >
                      <div className={`w-2 h-2 rounded-full ${getEventTypeColor(event.event_type)}`} />
                      <span className="truncate flex-1">{event.title}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Events List */}
          <div className="lg:col-span-2 space-y-6">
            {/* Upcoming Events */}
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
              <h2 className="section-title mb-6">Próximos Eventos</h2>
              
              {loading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-24 bg-[#F5F2EB] rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : upcomingEvents.length === 0 ? (
                <div className="text-center py-8">
                  <Trophy size={48} className="text-[#E6E2D8] mx-auto mb-4" />
                  <p className="text-[#73706A]">Nenhum evento próximo</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingEvents.map((event, index) => (
                    <div
                      key={event.id}
                      className="p-4 bg-[#F5F2EB] rounded-lg border border-transparent hover:border-[#E85D04] transition-colors"
                      data-testid={`event-card-${index}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-3 h-3 rounded-full ${getEventTypeColor(event.event_type)}`} />
                            <span className="badge badge-info capitalize">{event.event_type}</span>
                          </div>
                          <h3 className="font-medium text-lg text-[#1A1A1A]">{event.title}</h3>
                          {event.description && (
                            <p className="text-sm text-[#73706A] mt-1">{event.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-3 text-sm">
                            <span className="flex items-center gap-1 text-[#0077B6]">
                              <CalendarDots size={16} />
                              {format(new Date(event.date), "d 'de' MMM, yyyy", { locale: ptBR })}
                            </span>
                            {event.start_time && (
                              <span className="text-[#73706A]">
                                {event.start_time}{event.end_time && ` - ${event.end_time}`}
                              </span>
                            )}
                            {event.location && (
                              <span className="flex items-center gap-1 text-[#73706A]">
                                <MapPin size={16} />
                                {event.location}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => navigate('/courts')}
                            className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
                            data-testid={`event-register-${index}`}
                          >
                            Inscreva-se <ArrowRight size={14} className="ml-1" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(event)}
                            className="text-[#0077B6] hover:text-[#03045E] hover:bg-white"
                            data-testid={`edit-event-${index}`}
                          >
                            <Pencil size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDelete(event)}
                            className="text-[#9B2226] hover:text-[#9B2226] hover:bg-red-50"
                            data-testid={`delete-event-${index}`}
                          >
                            <Trash size={18} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="stat-card text-center">
                <p className="text-2xl font-semibold text-[#E85D04] font-['Outfit']">
                  {events.filter(e => e.event_type === 'competição').length}
                </p>
                <p className="text-sm text-[#73706A]">Competições</p>
              </div>
              <div className="stat-card text-center">
                <p className="text-2xl font-semibold text-[#0077B6] font-['Outfit']">
                  {events.filter(e => e.event_type === 'treino especial').length}
                </p>
                <p className="text-sm text-[#73706A]">Treinos Especiais</p>
              </div>
              <div className="stat-card text-center">
                <p className="text-2xl font-semibold text-[#2D6A4F] font-['Outfit']">
                  {events.filter(e => e.event_type === 'social').length}
                </p>
                <p className="text-sm text-[#73706A]">Eventos Sociais</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">
              {currentEvent ? 'Editar Evento' : 'Novo Evento'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Título *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Campeonato de Verão"
                  required
                  data-testid="event-title-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Descrição</Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field min-h-[80px] resize-none"
                  data-testid="event-description-input"
                />
              </div>
              <div>
                <Label>Tipo *</Label>
                <Select
                  value={formData.event_type}
                  onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                >
                  <SelectTrigger data-testid="event-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {eventTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <Label>Horário Início</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  data-testid="event-start-time-input"
                />
              </div>
              <div>
                <Label>Horário Fim</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  data-testid="event-end-time-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Local</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Ex: Praia de Copacabana"
                  data-testid="event-location-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#E85D04] hover:bg-[#DC2F02]"
                data-testid="save-event-btn"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">Confirmar exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-[#73706A] mt-2">
            Tem certeza que deseja remover o evento <strong>{currentEvent?.title}</strong>?
          </p>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-[#9B2226] hover:bg-[#7d1c1f]"
              data-testid="confirm-delete-event-btn"
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
